import { useState } from 'react';
import {
  Collapse,
  Paper,
  Box,
  Typography,
  IconButton,
  Button
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const SectionTips = ({
  section = 'esta sección',
  buttonTitle = '¿Cómo usar esta sección?',
  tipsTitle = '¿Cómo funciona esta sección?',
  tipsList = [],
  finalNote = '',
  color = '#0488AF',
  videoUrl = null,
  manualUrl = null
}) => {
  const [open, setOpen] = useState(false);

  return (
    <Box sx={{ my: 4 }}>
      <Button
        onClick={() => setOpen(!open)}
        sx={{
          border: `1px solid ${color}`,
          color: color,
          fontWeight: 600,
          borderRadius: 2,
          px: 3,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          '&:hover': {
            backgroundColor: `${color}10`
          }
        }}
        startIcon={<InfoIcon />}
        endIcon={
          <ExpandMoreIcon
            sx={{
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s ease'
            }}
          />
        }
      >
        {buttonTitle}
      </Button>

      <Collapse in={open} sx={{ mt: 2 }}>
        <Paper
          elevation={2}
          sx={{
            p: 3,
            borderRadius: '12px',
            backgroundColor: '#f8f9fa',
            borderLeft: `4px solid ${color}`
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: 1
            }}
          >
            <Typography variant="h6" sx={{ color: '#09274A' }}>
              <InfoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              {tipsTitle}
            </Typography>
            <IconButton size="small" onClick={() => setOpen(false)}>
              <ExpandMoreIcon
                sx={{
                  transform: 'rotate(180deg)',
                  transition: 'transform 0.3s ease'
                }}
              />
            </IconButton>
          </Box>

          <Typography variant="body1" paragraph sx={{ color: '#495057' }}>
            Aquí puedes encontrar consejos o instrucciones para usar {section}.
          </Typography>

          <Box
            component="ul"
            sx={{
              pl: 2,
              mb: 2,
              '& li': {
                mb: 1,
                typography: 'body1',
                color: '#495057'
              }
            }}
          >
            {tipsList.map((tip, i) => (
              <li key={i} dangerouslySetInnerHTML={{ __html: tip }} />
            ))}
          </Box>

          {finalNote && (
            <Typography
              variant="body2"
              sx={{
                fontStyle: 'italic',
                color: '#6c757d',
                fontWeight: 'bold',
                mb: 2
              }}
            >
              {finalNote}
            </Typography>
          )}

          {/* Sección de Tutoriales más detallados - Siempre visible */}
          <Box
            sx={{
              mt: 3,
              pt: 3,
              borderTop: '2px solid #e0e0e0'
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: '#09274A',
                mb: 2,
                fontSize: '1.1rem',
                fontWeight: 600
              }}
            >
              Tutoriales más detallados
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2
              }}
            >
              <Button
                variant="outlined"
                startIcon={<VideoLibraryIcon />}
                endIcon={<OpenInNewIcon />}
                href={videoUrl && videoUrl.trim() !== '' ? videoUrl : undefined}
                onClick={(e) => {
                  if (!videoUrl || videoUrl.trim() === '') {
                    e.preventDefault();
                    // No hacer nada si no hay URL, el botón está visible pero no funcional
                  }
                }}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  borderColor: videoUrl && videoUrl.trim() !== '' ? color : '#ccc',
                  color: videoUrl && videoUrl.trim() !== '' ? color : '#999',
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  textTransform: 'none',
                  cursor: videoUrl && videoUrl.trim() !== '' ? 'pointer' : 'default',
                  '&:hover': {
                    backgroundColor: videoUrl && videoUrl.trim() !== '' ? `${color}10` : 'transparent',
                    borderColor: videoUrl && videoUrl.trim() !== '' ? color : '#ccc'
                  }
                }}
                title={videoUrl && videoUrl.trim() !== '' ? 'Abrir video tutorial' : 'URL del video tutorial no configurada'}
              >
                Ver video tutorial
              </Button>
              <Button
                variant="outlined"
                startIcon={<MenuBookIcon />}
                endIcon={<OpenInNewIcon />}
                href={manualUrl && manualUrl.trim() !== '' ? manualUrl : undefined}
                onClick={(e) => {
                  if (!manualUrl || manualUrl.trim() === '') {
                    e.preventDefault();
                    // No hacer nada si no hay URL, el botón está visible pero no funcional
                  }
                }}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  borderColor: manualUrl && manualUrl.trim() !== '' ? color : '#ccc',
                  color: manualUrl && manualUrl.trim() !== '' ? color : '#999',
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  textTransform: 'none',
                  cursor: manualUrl && manualUrl.trim() !== '' ? 'pointer' : 'default',
                  '&:hover': {
                    backgroundColor: manualUrl && manualUrl.trim() !== '' ? `${color}10` : 'transparent',
                    borderColor: manualUrl && manualUrl.trim() !== '' ? color : '#ccc'
                  }
                }}
                title={manualUrl && manualUrl.trim() !== '' ? 'Abrir manual de usuario' : 'URL del manual de usuario no configurada'}
              >
                Manual de usuario
              </Button>
            </Box>
          </Box>
        </Paper>
      </Collapse>
    </Box>
  );
};

export default SectionTips;
